import {
  Box,
  Typography,
  Sheet,
  Stack,
  FormControl,
  FormLabel,
  Textarea,
  Button,
} from '@mui/joy';
import { FC, FormEvent, JSX, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Input, Loader, Dropdown } from '@consolidocs-ui/components';
import { useToast } from '@consolidocs-ui/utils';
import {
  useGetIndustryQuery,
  useUpdateIndustriesMutation,
} from '@consolidocs-ui/data-access/IndustryApi';
import { useGetContractsQuery } from '@consolidocs-ui/data-access/ContractApi';
import { PageBreadcrumbs } from '@consolidocs-ui/components';
import { FieldErrorType, handleApiErrors } from '@consolidocs-ui/utils';
import { IndustryFormType } from '@consolidocs-ui/types';
import { ContractType } from '@consolidocs-ui/types';
import { industryInitialState } from '../CreateIndustry/CreateIndustry';

type ErrorType = {
  data: {
    message: string;
  };
};

export const EditIndustry: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { industryId } = useParams<{ industryId: string }>();

  const {
    data: getIndustry,
    isLoading,
    refetch,
  } = useGetIndustryQuery(industryId ?? '');

  const { data: contractTypes, isLoading: isContractLoading } =
    useGetContractsQuery();

  const [industryForm, setIndustryForm] =
    useState<IndustryFormType>(industryInitialState);

  const [errors, setErrors] = useState<FieldErrorType>({});
  const [updateIndustry, { isLoading: isLoadingUpdate }] =
    useUpdateIndustriesMutation();
  const [triggerToast] = useToast();

  const handleChange = (
    field: keyof IndustryFormType,
    value: string | string[]
  ) => {
    setIndustryForm({ ...industryForm, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: FieldErrorType = {};

    if (!industryForm?.name.trim()) {
      newErrors.name = 'Industry name is required';
    }

    if (!industryForm?.code.trim()) {
      newErrors.code = 'Industry code is required';
    }

    if (Object.keys(newErrors).length > 0) {
      return setErrors(newErrors);
    }

    try {
      await updateIndustry(industryForm).unwrap();
      triggerToast({
        message: 'Industry updated successfully',
        type: 'success',
      });
      await refetch();
      navigate('/industries');
    } catch (err) {
      console.error('Failed to update industry:', err);
      handleApiErrors<IndustryFormType>(
        err,
        setErrors,
        (message: string, color: 'success' | 'danger') =>
          triggerToast({ message, type: color }),
        (err as ErrorType)?.data?.message || 'Failed to update industry'
      );
    }
  };

  useEffect(() => {
    if (getIndustry?.data) {
      const industryData = getIndustry.data;

      // Ensure contract_types is an array of IDs
      const normalizedContractTypes = Array.isArray(industryData.contract_types)
        ? industryData.contract_types.map((ct) =>
            typeof ct === 'string' ? ct : ct.id
          )
        : [];

      setIndustryForm({
        ...industryData,
        contract_types: normalizedContractTypes,
      });
    }
  }, [getIndustry]);

  const contractDropdownOptions =
    contractTypes?.data.map((type: ContractType) => ({
      label: type.name,
      value: type.id,
    })) || [];

  return (
    <Box sx={{ flex: 1, width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 995,
          bgcolor: '#f5f5f5',
          // Cancel the .content wrapper's top padding (p-4) so the bar sticks
          // flush to the top instead of drifting down before it engages.
          mt: { md: -2 },
          pt: { xs: 1, md: 2 },
          pb: 1,
        }}
      >
        <PageBreadcrumbs
          items={[
            { label: 'All Industries', path: '/industries' },
            { label: 'Edit Industry' },
          ]}
        />
      </Box>

      <Sheet
          variant="outlined"
          sx={{
            width: { xs: '100%', sm: '90%', md: '800px' },
            maxWidth: '800px',
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 'md',
            boxShadow: 'sm',
            borderColor: 'neutral.outlinedBorder',
            marginTop: { xs: '12px', md: '20px' },
          }}
        >
          <Typography
            level="h4"
            mb={2}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            Edit Industry
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mx: 'auto', mt: 4 }}
          >
            {isLoading ? (
              <Loader />
            ) : (
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>Industry Code</FormLabel>
                  <Input
                    value={industryForm?.code ?? ''}
                    onChange={(e) => handleChange('code', e.target.value)}
                    placeholder="e.g., MANUFACTURING"
                    required
                    error={!!errors['code']}
                    errorMessage={errors['code']}
                    size="sm"
                  />
                </FormControl>

                <FormControl required>
                  <FormLabel>Industry Name</FormLabel>
                  <Input
                    value={industryForm?.name ?? ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Manufacturing"
                    required
                    error={!!errors['name']}
                    errorMessage={errors['name']}
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    minRows={3}
                    value={industryForm?.description ?? ''}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    placeholder="Optional description"
                    size="sm"
                  />
                </FormControl>

                <Dropdown
                  label="Contract Types"
                  options={contractDropdownOptions}
                  multiple
                  value={industryForm.contract_types}
                  onChange={(_, newValue) =>
                    handleChange('contract_types', newValue)
                  }
                  isLoading={isContractLoading}
                  error={errors.contract_types}
                />

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    type="button"
                    variant="plain"
                    color="neutral"
                    onClick={() => navigate('/industries')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="solid"
                    color="primary"
                    loading={isLoadingUpdate}
                    loadingIndicator={<Loader size="sm" />}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </Sheet>
    </Box>
  );
};
